<template>
	<div class="save-design-container" v-if="magentoData.customerId && magentoData.saveDesignApiUrl">
		<button
			id="visualizer--save-design"
			:class="{failure: failure, success: success, ready: ready}"
			class="save-design"
			:disabled="processing"
			@click="saveDesign"
		>
			<span v-text="saveText"></span>
		</button>
		<div
			class="save-design-error"
			:aria-hidden="!failure"
			:class="{show: failure}"
		>Oops, something went wrong while saving your desing. Please try again later.</div>
		<div class="saved-design" v-if="saved && baseUrl">
			You can find your saved designs
			<a :href="mySavedDesignsUrl" target="_blank">here</a>.
		</div>
	</div>
</template>

<script>
import { mapState } from "vuex";
import helper from "@/mixins/helper.js";
import cartItem from "@/mixins/cart-item.js";
import axios from "axios";
import {isArray} from "vue-resource/src/util";

export default {
	name: "SaveDesign",
	mixins: [helper, cartItem],
	data: function() {
		return {
			cartItem: {},
			processing: false, // Saving - button is disabled
			failure: false, // Show error message
			success: false, // Show success message
			saved: false, // Design is saved at least once
			ready: true, // Button can be pressed again
			saveText: "Save Design" // Text shown in the button
		};
	},
	computed: {
		...mapState({
			config: state => state.configurator.config,
			magentoData: state => state.configurator.magentoData,
			exportedImage: state => state.configurator.exportedImage
		}),
		baseUrl() {
			return window.location.origin;
		},
		mySavedDesignsUrl() {
			return this.baseUrl + "/mydesigns/saved/";
		},
		saveDesignHeaders() {
			return {
				headers: {
					"Content-Type": "application/x-www-form-urlencoded; charset=UTF-8"
				}
			};
		}
	},
	methods: {
		savedNotification() {
			let self = this;

			// Show success/error message for 2 seconds
			self.saveText = self.failure ? "Failure!" : "Saved!";

			// After 3 seconds, hide text
			setTimeout(function() {
				self.ready = false;
				self.failure = false;
				self.success = false;

				// After .5 seconds, reset the button
				setTimeout(function() {
					self.saveText = "Save Design";
					self.ready = true;
					self.processing = false;
				}, 500);
			}, 3000);
		},
		createDesignPayload() {

			let custom_options = [];
			for (let key of Object.keys(this.selectedOptions)) {
				let option = this.selectedOptions[key];
				let value = option.sku;
				let front = option.previewMutations.front;
				if (isArray(front) && front[0].property === 'text') {
					value = option.value;
				}
				custom_options.push({
					optionId: key,
					selectedValueId: value
				});
			}
			let formData = new FormData();
			formData.append('pid', this.magentoData.sku);
			formData.append('customerId', this.magentoData.customerId);
			formData.append('quantity', this.quantity || 1);
			formData.append('options', JSON.stringify(custom_options));
			formData.append('preview', this.exportedImage);
			return formData;

		},
		saveDesign() {
			let self = this;
			let payload = this.createDesignPayload();
			const http = axios.create();

			this.processing = true;
			this.saveText = "Saving...";
			http.post(self.magentoData.saveDesignApiUrl, payload, self.saveDesignHeaders)
				.then(function() {
					self.$store.dispatch("configurator/increaseSavedDesignAmount"); // For GTM
					self.success = true;
					self.saved = true;
				})
				.catch(function(error) {
					self.failure = true;
					try {
						console.error("Saving the design failed!");
					} catch (e) {
						console.error("Saving the design failed!", e);
					}
				})
				.finally(function() {
					self.savedNotification();
				});
		}
	}
};
</script>

<style lang="scss" scoped>
#seabags-configurator {
	.save-design-container {
		margin-bottom: 32px;

		button.save-design {
			background: transparent;
			border: 1px solid $color-martinique-blue;
			color: $color-martinique-blue;
			-webkit-text-fill-color: $color-martinique-blue; // all:unset changes the text color on Safari
			width: 100%;
			transform: translate3d(0, 0, 0);
			-webkit-transform: translate3d(0, 0, 0);
			backface-visibility: hidden;
			overflow: hidden;
			transition: border-color 500ms, opacity 500ms;
			animation-iteration-count: 1;

			&:disabled {
				opacity: 1;
				border-color: $border-color-dimm;
				cursor: not-allowed;
			}

			> span {
				position: relative;
				top: -30px;
				transition: top 500ms, opacity 400ms;
				opacity: 0;
			}

			&.ready {
				> span {
					top: 0;
					opacity: 1;
				}
			}

			&.failure {
				color: $color-red;
				-webkit-text-fill-color: $color-red; // all:unset changes the text color on Safari
				animation: shake 0.82s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
			}

			&.success {
				color: $color-green;
				-webkit-text-fill-color: $color-green; // all:unset changes the text color on Safari
				animation: pump 300ms ease-in both;
			}
		}

		.save-design-error {
			color: $color-red;
			padding: 8px;
			font-size: 14px;
			text-align: center;
			opacity: 0;
			visibility: hidden;
			transition: transform 500ms linear 0ms, opacity 500ms linear 0ms, visibility 500ms linear 0ms, max-height 0ms linear 500ms;
			transform: rotateX(-90deg);
			max-height: 0;

			&.show {
				visibility: visible;
				transform: none;
				opacity: 1;
				max-height: 50px;
				transition: transform 500ms linear 0ms, opacity 500ms linear 0ms, visibility 500ms linear 0ms;
			}
		}

		.saved-design {
			text-align: center;
			font-size: 14px;
		}
	}

	@-webkit-keyframes shake {
		10%,
		90% {
			-webkit-transform: translate3d(-1px, 0, 0);
		}

		20%,
		80% {
			-webkit-transform: translate3d(2px, 0, 0);
		}

		30%,
		50%,
		70% {
			-webkit-transform: translate3d(-4px, 0, 0);
		}

		40%,
		60% {
			-webkit-transform: translate3d(4px, 0, 0);
		}
	}
	@keyframes shake {
		10%,
		90% {
			transform: translate3d(-1px, 0, 0);
		}

		20%,
		80% {
			transform: translate3d(2px, 0, 0);
		}

		30%,
		50%,
		70% {
			transform: translate3d(-4px, 0, 0);
		}

		40%,
		60% {
			transform: translate3d(4px, 0, 0);
		}
	}
	@keyframes pump {
		0% {
			transform: scale(1);
		}
		30% {
			transform: scale(1.1);
		}
		100% {
			transform: scale(1);
		}
	}
}
</style>